import { useTranslation } from "react-i18next";
import Button from "../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../components/button/types";
import { MyAreaTitle } from "../../components";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { ReactComponent as CloseIcon } from "../../../../../../assets/close.svg";
import {
  checkIfCreditExists,
  fetchCoaches,
  fetchDefaultCvInfo,
  fetchLinkedInCredits,
  submitLinkedInPackageRequest,
} from "../../../../../../services/linkedin-package.service";
import { DragAndDropUploaderModifiedForCoverLetterPopUp } from "../../../../../../components/DragAndDropUploader/DragAndDropModifiedForCoverLetterPopUp";
import { CircularImage } from "../../../../../../components/circularImage";
import { LinkedInCreditsTable } from "./components/LinkedinCreditTable";
import Lottie from "lottie-react";
import linkedRequestAnimation from "./../../../../../../assets/linkedin-request.json";

export interface Coach {
  id: number;
  name: string;
  profilePicture: string;
  platformContentId: string;
}

export interface DefaultCvInfo {
  hasDefaultCv: boolean;
  cvName?: string;
  cvKey?: string;
}

export interface LinkedInSubmitData {
  linkedInUrl: string;
  cvFile: File | null;
  useDefaultCv: boolean;
  proceedWithoutCv: boolean;
  coachId: string;
}

export function LinkedInPackage() {
  const { t } = useTranslation("linkedin");
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [linkedInUrl, setLinkedInUrl] = useState("");
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [selectedCvOption, setSelectedCvOption] = useState<
    "upload" | "default" | "none"
  >("upload");
  const [selectedCoach, setSelectedCoach] = useState<Coach | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [isError, setIsError] = useState<boolean>(false);

  const { data: defaultCvData, isLoading: isLoadingCv } =
    useQuery<DefaultCvInfo | null>("defaultCvInfo", fetchDefaultCvInfo, {});

  const {
    data: hasExistingCredit,
    isLoading: isLoadingCreditCheck,
    refetch: refetchChecks,
  } = useQuery<any>("hasExistingCredit", checkIfCreditExists, {});

  const { data: coaches, isLoading: isLoadingCoaches } = useQuery<Coach[]>(
    "availableCoaches",
    fetchCoaches,
    {
      staleTime: 300000,
    }
  );

  // --- API Mutation ---
  const submitMutation = useMutation(submitLinkedInPackageRequest, {
    onMutate: () => {
      setIsSubmitting(true);
      setMessage(null);
      setIsError(false);
      setStep(5);
    },
    onSuccess: () => {
      setIsSubmitting(false);
      setMessage(t("submit.success"));
      setIsError(false);
      setTimeout(() => {
        refetchChecks();
      }, 2500);
    },
    onError: (error: any) => {
      setIsSubmitting(false);
      setMessage(t("submit.failure"));
      setIsError(true);
      setTimeout(() => {
        clearState();
      }, 2500);
    },
    onSettled: () => {},
  });

  useEffect(() => {
    if (coaches && coaches.length > 0 && !selectedCoach) {
      setSelectedCoach(coaches[0]);
    }
  }, [coaches, selectedCoach]);

  useEffect(() => {
    if (selectedCvOption === "default" || selectedCvOption === "none") {
      setCvFile(null);
      setFileError(null);
    }
  }, [selectedCvOption]);

  useEffect(() => {
    if (
      selectedCvOption === "default" &&
      !isLoadingCv &&
      !defaultCvData?.hasDefaultCv
    ) {
      setSelectedCvOption("upload");
    }
  }, [selectedCvOption, defaultCvData, isLoadingCv]);

  const validateUrl = (url: string): boolean => {
    const trimmedUrl = url.trim();

    if (!trimmedUrl) {
      setUrlError(null);
      return true;
    }
    const linkedInPattern =
      /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]+\/?$/;
    if (!linkedInPattern.test(url.trim())) {
      setUrlError(t("step1.validation.invalid"));
      return false;
    }
    setUrlError(null);
    return true;
  };

  const handleOpenModal = () => {
    clearState();
    setIsOpen(true);
    setStep(1);
    if (defaultCvData?.hasDefaultCv) {
      setSelectedCvOption("default");
    } else {
      setSelectedCvOption("upload");
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    clearState();
  };

  const clearState = () => {
    setStep(1);
    setLinkedInUrl("");
    setCvFile(null);
    setSelectedCvOption("upload");
    setSelectedCoach(coaches && coaches.length > 0 ? coaches[0] : null);
    setFileError(null);
    setUrlError(null);
    setMessage(null);
    setIsError(false);
    setIsSubmitting(false);
    if (defaultCvData?.hasDefaultCv) {
      setSelectedCvOption("default");
    }
  };

  const handleStep1Next = () => {
    if (validateUrl(linkedInUrl)) {
      setStep(2);
    }
  };
  const handleStep2Next = () => {
    if (selectedCvOption === "upload" && (!cvFile || fileError)) {
      if (!cvFile) setFileError(t("step2.validation.fileRequired"));
      return;
    }
    if (selectedCvOption !== "upload") {
      setFileError(null);
    }
    setStep(3);
  };

  const tab = useMemo(() => {
    const baseTab = ["your-linkedin-packages"];
    return baseTab;
  }, []);

  const handleSelectCoach = (coach: Coach) => {
    setSelectedCoach(coach);
    setStep(3);
  };

  const handleSubmit = () => {
    if (!selectedCoach || isSubmitting) {
      console.error(
        "Submission attempt without selected coach or while submitting."
      );
      return;
    }

    const submissionData: LinkedInSubmitData = {
      linkedInUrl: linkedInUrl.trim(),
      cvFile: selectedCvOption === "upload" ? cvFile : null,
      useDefaultCv: selectedCvOption === "default",
      proceedWithoutCv: selectedCvOption === "none",
      coachId: selectedCoach.platformContentId,
    };

    submitMutation.mutate(submissionData);
  };

  const canChangeCoach = useMemo(
    () => coaches && coaches.length > 1,
    [coaches]
  );

  const getCvSelectionText = () => {
    switch (selectedCvOption) {
      case "upload":
        return cvFile ? cvFile.name : t("step3.cvInfo.noneSelected");
      case "default":
        return defaultCvData?.cvName || t("step3.cvInfo.defaultError");
      case "none":
        return t("step3.cvInfo.proceedWithout");
      default:
        return "";
    }
  };

  const ensureProtocol = (url?: string): string | undefined => {
    if (!url) return undefined;
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    if (url.startsWith("//")) {
      return `https:${url}`;
    }

    return undefined;
  };

  return (
    <>
      <div className="hidden lg:flex">
        <MyAreaTitle title={t("page.title")} />
      </div>
      <div className="mb-6 ">
        <span>{t(`page.description`)}</span>
      </div>
      <div className="mb-4">
        <div className="">
          <p>{t("page.description2-part1")}</p>
        </div>

        {/* List section */}

        <ul className="space-y-1 list-none">
          <li className="flex">
            <div className="w-4 h-4 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
              <div className="w-[6px] h-[6px] bg-black rounded-full"></div>
            </div>
            <div>
              <span className="font-bold">
                {t(`page.description2-list.bold1`)}
              </span>{" "}
              <span>{t(`page.description2-list.normal1`)}</span>
            </div>
          </li>
          <li className="flex">
            <div className="w-4 h-4 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
              <div className="w-[6px] h-[6px] bg-black rounded-full"></div>
            </div>
            <div>
              <span className="font-bold">
                {t(`page.description2-list.bold2`)}
              </span>{" "}
              <span>{t(`page.description2-list.normal2`)}</span>
            </div>
          </li>
          <li className="flex">
            <div className="w-4 h-4 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
              <div className="w-[6px] h-[6px] bg-black rounded-full"></div>
            </div>
            <div>
              <span className="font-bold">
                {t(`page.description2-list.bold3`)}
              </span>{" "}
              <span>{t(`page.description2-list.normal3`)}</span>
            </div>
          </li>
          <li className="flex">
            <div className="w-4 h-4 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
              <div className="w-[6px] h-[6px] bg-black rounded-full"></div>
            </div>
            <div>
              <span className="font-bold">
                {t(`page.description2-list.bold4`)}
              </span>{" "}
              <span>{t(`page.description2-list.normal4`)}</span>
            </div>
          </li>
        </ul>
      </div>
      <div className="flex justify-end">
        {" "}
        <Button
          className="mb-2"
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.SMALL}
          onClick={handleOpenModal}
          disabled={
            isLoadingCreditCheck ||
            hasExistingCredit?.exists === true ||
            hasExistingCredit?.enoughCredits === false
          }
        >
          {t("page.buttonText")}
        </Button>
      </div>
      {hasExistingCredit?.exists === false &&
        hasExistingCredit?.enoughCredits === false && (
          <div className="text-Grey-dark flex justify-end">
            {" "}
            {t("notEnoughCredits")}
          </div>
        )}
      {hasExistingCredit?.exists === true && (
        <div className="text-Grey-dark flex justify-end">
          {" "}
          {t("ongoingRequest")}
        </div>
      )}

      {/* {linkedInCredits && linkedInCredits?.length > 0 && (
        <>
          <div className="flex w-full">
            {tab.map((tab) => (
              <div
                key={tab}
                className={`pt-6 pb-0 pl-0 pr-4 cursor-pointer font-bold`}
              >
                <span className="p-2 pb-0 mb-2 flex">
                  {t(`assessments.cover-letter.${tab}`)}
                </span>

                <div className="h-[2px] bg-Yellow-default w-full" />
              </div>
            ))}
          </div>
          <hr />

          <>
            <LinkedInCreditsTable
              credits={linkedInCredits}
              refetchCredits={refetchCredits}
              setCreditsAmount={setExpandedRow}
            />
          </>
        </>
      )} */}
      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white p-6 lg:rounded-lg shadow-lg lg:w-1/3 w-full h-full lg:h-auto">
            {/* Step 1: LinkedIn URL */}
            {step === 1 && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-bold">{t("step1.title")}</span>
                  <CloseIcon
                    onClick={handleCancel}
                    className="cursor-pointer"
                  />
                </div>
                <p className="mb-2">{t("step1.description1")}</p>
                <p className="mb-4">{t("step1.description2")}</p>

                <label htmlFor="linkedInUrl" className="block mb-1 font-medium">
                  {t("step1.label")}
                </label>
                <input
                  id="linkedInUrl"
                  type="url"
                  placeholder={t("step1.placeholder") as string}
                  value={linkedInUrl}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLinkedInUrl(value);

                    if (urlError && value) {
                      validateUrl(value);
                    } else if (!value) {
                      setUrlError(null);
                    }
                  }}
                  onBlur={() => validateUrl(linkedInUrl)} // Validate on blur
                  className={`mb-1 border p-2 w-full rounded-md ${
                    urlError ? "border-red-500" : "border-gray-300"
                  }`}
                  aria-invalid={!!urlError}
                  aria-describedby="url-error"
                />
                {urlError && (
                  <p id="url-error" className="text-red-600 text-sm mb-4">
                    {urlError}
                  </p>
                )}

                {/* Buttons */}
                <div
                  className="w-full py-3 px-4 lg:py-0 lg:px-0 mt-4
  fixed bottom-0 left-0 bg-white 
  lg:relative lg:bottom-auto lg:left-auto lg:bg-transparent"
                >
                  <div className="flex items-center justify-between">
                    <Button
                      className="w-full mr-2"
                      variant={ButtonVariant.SECONDARY}
                      size={ButtonSize.SMALL}
                      onClick={handleCancel}
                    >
                      {t("cancel")}
                    </Button>
                    <Button
                      className="w-full ml-2"
                      variant={ButtonVariant.PRIMARY}
                      size={ButtonSize.SMALL}
                      onClick={handleStep1Next}
                      disabled={!!urlError}
                    >
                      {t("next")}
                    </Button>
                  </div>{" "}
                </div>
              </>
            )}

            {/* Step 2: CV Selection */}
            {step === 2 && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-bold">{t("step2.title")}</span>
                  <CloseIcon
                    onClick={handleCancel}
                    className="cursor-pointer"
                  />
                </div>
                <p className="mb-4">{t("step2.description")}</p>

                <div className="space-y-4">
                  {/* Option 1: Upload CV */}
                  <div>
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="radio"
                        name="cvOption"
                        value="upload"
                        checked={selectedCvOption === "upload"}
                        onChange={() => setSelectedCvOption("upload")}
                        className="hidden"
                      />
                      <div className="w-5 h-5 border-2 rounded-full flex items-center justify-center border-Yellow-default mr-2 mt-px flex-shrink-0">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            selectedCvOption === "upload"
                              ? "bg-Yellow-default"
                              : "bg-white"
                          } transition-all`}
                        />
                      </div>
                      <span
                        className={`${
                          selectedCvOption === "upload" ? "font-bold" : ""
                        }`}
                      >
                        {t("step2.optionUpload")}
                      </span>
                    </label>
                    {selectedCvOption === "upload" && (
                      <div className="ml-7 ">
                        <DragAndDropUploaderModifiedForCoverLetterPopUp
                          singleFile
                          filesSetter={(files) => {
                            if (files.length > 0) {
                              const file = files[0];
                              const maxSize = 2 * 1024 * 1024;
                              const allowedTypes = [
                                "application/pdf",
                                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                                "application/msword",
                              ]; // PDF, DOCX, DOC

                              if (file.size > maxSize) {
                                setFileError(
                                  t("step2.validation.sizeError", {
                                    maxSize: "2MB",
                                  })
                                );
                                setCvFile(null);
                              } else if (!allowedTypes.includes(file.type)) {
                                setFileError(t("step2.validation.typeError"));
                                setCvFile(null);
                              } else {
                                setCvFile(file);
                                setFileError(null);
                              }
                            } else {
                              setCvFile(null);
                            }
                          }}
                          currentFile={cvFile}
                        />
                        {fileError && (
                          <p className="text-sm text-red-600 mt-1">
                            {fileError}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Option 2: Use Default CV (Conditional) */}
                  {isLoadingCv && (
                    <p className="text-gray-500 ml-7">
                      {t("step2.loadingDefaultCv")}
                    </p>
                  )}
                  {!isLoadingCv && defaultCvData?.hasDefaultCv && (
                    <div>
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="radio"
                          name="cvOption"
                          value="default"
                          checked={selectedCvOption === "default"}
                          onChange={() => setSelectedCvOption("default")}
                          className="hidden"
                        />
                        <div className="w-5 h-5 border-2 rounded-full flex items-center justify-center border-Yellow-default mr-2 mt-px flex-shrink-0">
                          <div
                            className={`w-3 h-3 rounded-full ${
                              selectedCvOption === "default"
                                ? "bg-Yellow-default"
                                : "bg-white"
                            } transition-all`}
                          />
                        </div>
                        <span
                          className={`${
                            selectedCvOption === "default" ? "font-bold" : ""
                          }`}
                        >
                          {t("step2.optionDefault")}
                        </span>
                      </label>
                      {selectedCvOption === "default" &&
                        defaultCvData.cvName && (
                          <p className="ml-7 mt-1 text-[#757575] break-all">
                            {defaultCvData.cvName}
                          </p>
                        )}
                    </div>
                  )}

                  {/* Option 3: Proceed Without CV */}
                  <div>
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="radio"
                        name="cvOption"
                        value="none"
                        checked={selectedCvOption === "none"}
                        onChange={() => setSelectedCvOption("none")}
                        className="hidden"
                      />
                      <div className="w-5 h-5 border-2 rounded-full flex items-center justify-center border-Yellow-default mr-2 mt-px flex-shrink-0">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            selectedCvOption === "none"
                              ? "bg-Yellow-default"
                              : "bg-white"
                          } transition-all`}
                        />
                      </div>
                      <span
                        className={`${
                          selectedCvOption === "none" ? "font-bold" : ""
                        }`}
                      >
                        {t("step2.optionNone")}
                      </span>
                    </label>
                  </div>
                </div>

                <div
                  className="w-full py-3 px-4 lg:py-0 lg:px-0 mt-4
  fixed bottom-0 left-0 bg-white 
  lg:relative lg:bottom-auto lg:left-auto lg:bg-transparent"
                >
                  <div className="flex items-center justify-between">
                    <Button
                      className="w-full mr-2"
                      variant={ButtonVariant.SECONDARY}
                      size={ButtonSize.SMALL}
                      onClick={() => setStep(1)}
                    >
                      {t("back")}
                    </Button>
                    <Button
                      variant={ButtonVariant.PRIMARY}
                      size={ButtonSize.SMALL}
                      onClick={handleStep2Next}
                      className="w-full ml-2"
                      disabled={
                        selectedCvOption === "upload" &&
                        (!cvFile || !!fileError)
                      }
                    >
                      {t("next")}
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-bold">{t("step3.title")}</span>
                  <CloseIcon
                    onClick={handleCancel}
                    className="cursor-pointer"
                  />
                </div>
                <p className="mb-4">{t("step3.description")}</p>

                <div>
                  <div className="">
                    <span className="block font-semibold">
                      {t("step3.linkedinUrlLabel")}:{" "}
                    </span>
                    <span className="block text-[#757575] break-all mb-2">
                      {linkedInUrl}
                    </span>
                  </div>
                  <div>
                    <span className="block font-semibold">
                      {t("step3.cvInfoLabel")}:{" "}
                    </span>
                    <span className="block text-[#757575] break-all mb-2">
                      {getCvSelectionText()}
                    </span>
                  </div>
                  <div>
                    <span className="block font-semibold">
                      {t("step3.coachLabel")}:{" "}
                    </span>
                    {isLoadingCoaches ? (
                      <span className="text-gray-500">
                        {t("step3.loadingCoach")}
                      </span>
                    ) : selectedCoach ? (
                      <span className="block text-[#757575] mb-2">
                        {selectedCoach.name}
                      </span>
                    ) : (
                      <span className="text-red-600">
                        {t("step3.noCoachAssigned")}
                      </span>
                    )}
                  </div>
                </div>

                {/* Buttons */}

                <div>
                  {/* Button to change coach */}
                  {canChangeCoach && !isLoadingCoaches && (
                    <button
                      onClick={() => setStep(4)}
                      className="items-center underline text-[#757575] font-semibold hover:text-gray-700 focus:outline-none"
                    >
                      {t("step3.changeCoachButton")}
                    </button>
                  )}
                </div>
                <div
                  className="w-full py-3 px-4 lg:py-0 lg:px-0 mt-4
  fixed bottom-0 left-0 bg-white 
  lg:relative lg:bottom-auto lg:left-auto lg:bg-transparent"
                >
                  <div className="flex items-center justify-between">
                    <Button
                      className="w-full mr-2"
                      variant={ButtonVariant.SECONDARY}
                      size={ButtonSize.SMALL}
                      onClick={() => setStep(2)}
                      disabled={isSubmitting}
                    >
                      {t("back")}
                    </Button>
                    <Button
                      className="w-full ml-2"
                      variant={ButtonVariant.PRIMARY}
                      size={ButtonSize.SMALL}
                      onClick={handleSubmit}
                      disabled={
                        isSubmitting || isLoadingCoaches || !selectedCoach
                      } // Disable if submitting, coaches loading, or no coach selected
                    >
                      {isSubmitting ? t("submitting") : t("confirm")}
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* Step 4: Coach Selection (Optional) */}
            {step === 4 && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-bold">{t("step4.title")}</span>
                  <CloseIcon
                    onClick={handleCancel}
                    className="cursor-pointer"
                  />
                </div>
                <p className="mb-6">{t("step4.description")}</p>

                {isLoadingCoaches && <p>{t("step3.loadingCoach")}</p>}
                {!isLoadingCoaches && coaches && coaches.length > 0 && (
                  <div className="space-y-2">
                    {coaches.map((coach) => {
                      const profilePicUrl = ensureProtocol(
                        coach.profilePicture
                      );
                      const isSelected = selectedCoach?.id === coach.id;
                      return (
                        <label
                          key={coach.id}
                          className={`flex items-center cursor-pointer p-3 border rounded-md transition-colors duration-150 ease-in-out ${
                            isSelected
                              ? "bg-yellow-50 border-Yellow-default shadow-sm"
                              : "hover:bg-gray-50 border-gray-200"
                          }`}
                        >
                          {/* Hidden actual radio input */}
                          <input
                            type="radio"
                            name="coachSelection"
                            value={coach.id}
                            checked={isSelected}
                            onChange={() => handleSelectCoach(coach)}
                            className="hidden"
                          />

                          {/* Custom Radio Button Visual */}
                          <div
                            className={`w-5 h-5 border-2 rounded-full flex items-center justify-center border-Yellow-default mr-4 flex-shrink-0`} // Increased margin
                            style={{ aspectRatio: "1" }}
                          >
                            <div
                              className={`w-3 h-3 rounded-full ${
                                isSelected ? "bg-Yellow-default" : "bg-white"
                              } transition-all`}
                            />
                          </div>

                          {/* Profile Picture */}
                          <div className="w-10 h-10 mr-4 overflow-hidden rounded-full flex-shrink-0 border border-gray-200">
                            {" "}
                            <CircularImage
                              src={profilePicUrl!}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Coach Name */}
                          <span
                            className={`font-medium ${
                              isSelected ? "text-gray-800" : "text-gray-700"
                            }`}
                          >
                            {coach.name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {!isLoadingCoaches && !coaches?.length && (
                  <p className="text-red-600">{t("step4.noCoachesFound")}</p>
                )}

                {/* Buttons */}
                <div
                  className="w-full py-3 px-4  lg:py-0 lg:px-0 mt-4
  fixed bottom-0 left-0 bg-white 
  lg:relative lg:bottom-auto lg:left-auto lg:bg-transparent"
                >
                  <div className="flex justify-end space-x-2 pt-6">
                    <Button
                      className="w-1/2"
                      variant={ButtonVariant.SECONDARY}
                      size={ButtonSize.SMALL}
                      onClick={() => setStep(3)}
                    >
                      {t("back")}
                    </Button>
                  </div>{" "}
                </div>
              </>
            )}

            {/* Step 5: Loading / Result */}
            {step === 5 && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-bold">
                    {isSubmitting
                      ? t("step5.submittingTitle")
                      : isError
                      ? t("step5.errorTitle")
                      : t("step5.successTitle")}
                  </span>
                  {/* Allow closing only after submission attempt is finished */}
                  {!isSubmitting && (
                    <CloseIcon
                      onClick={handleCancel}
                      className="cursor-pointer"
                    />
                  )}
                </div>

                <div className="flex flex-col items-center justify-center min-h-[200px] text-center">
                  <>
                    <Lottie
                      animationData={linkedRequestAnimation}
                      loop={false} //loop={isSubmitting}
                      style={{ width: "146px", height: "146px" }}
                    />
                    {/* <p className="mt-4">{t("step5.submittingText")}</p> */}
                  </>

                  {!isSubmitting && message && (
                    <p
                      className={`mt-4 text-lg ${
                        isError ? "text-red-700" : "text-gray-600"
                      }`}
                    >
                      {message}
                    </p>
                  )}
                </div>
                {/* Optionally add a button to close manually if needed, especially on error */}
                {/* {!isSubmitting && isError && (
                        <div className="flex justify-end space-x-2 mt-6">
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.SMALL}
                                onClick={handleCancel}
                            >
                                {tcommon("close")}
                            </Button>
                        </div>
                   )} */}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
